'use client'

import { useMemo, useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { Input } from '@/components/ui/input'

export type PreparedBallotVocabTerm = {
  id: string
  name: string
  definition: string
  searchText: string
}

export type PreparedBallotVocabGroup = {
  categoryId: string
  categoryName: string
  terms: PreparedBallotVocabTerm[]
}

type BallotVocabClientProps = {
  groups: PreparedBallotVocabGroup[]
  searchPlaceholder?: string | null
}

export function BallotVocabClient({ groups, searchPlaceholder }: BallotVocabClientProps) {
  const [query, setQuery] = useState('')
  const normalizedQuery = query.trim().toLowerCase()
  const placeholder = searchPlaceholder?.trim() || 'Search ballot vocab...'

  const filteredGroups = useMemo(() => {
    if (!normalizedQuery) return groups

    return groups
      .map((group) => ({
        ...group,
        terms: group.terms.filter((term) => term.searchText.includes(normalizedQuery)),
      }))
      .filter((group) => group.terms.length > 0)
  }, [groups, normalizedQuery])

  if (!groups.length) return null

  return (
    <Card className="w-full">
      <CardHeader>
        <Input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={placeholder}
          aria-label={placeholder}
        />
      </CardHeader>

      <CardContent className="divide-y divide-black p-0">
        {filteredGroups.length === 0 ? (
          <p className="p-4 md:p-6">No ballot vocab terms match &ldquo;{query}&rdquo;.</p>
        ) : (
          filteredGroups.map((group) => (
            <section key={group.categoryId}>
              <div className="border-b border-black bg-neutral-100 px-4 py-2 md:px-6">
                <CardTitle>{group.categoryName}</CardTitle>
              </div>
              <div className="divide-y divide-black">
                {group.terms.map((term) => (
                  <Collapsible key={term.id} className="group" defaultOpen={false}>
                    <CollapsibleTrigger asChild>
                      <div className="group-data-[state=open]:border-b group-data-[state=open]:border-black group-data-[state=open]:bg-neutral-100 flex w-full cursor-pointer flex-row items-center justify-between gap-3 bg-white p-4 py-3 font-bold transition-colors hover:bg-neutral-100 md:px-6">
                        <CardTitle className="min-w-0 flex-1 text-left">{term.name}</CardTitle>
                        <ChevronDown
                          aria-hidden="true"
                          className="shrink-0 transition-transform duration-200 -rotate-90 group-data-[state=open]:rotate-0"
                        />
                      </div>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="px-4 pb-4 pt-4 md:px-6">
                        <p>{term.definition}</p>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                ))}
              </div>
            </section>
          ))
        )}
      </CardContent>
    </Card>
  )
}
